import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { SettingsPage } from "../../SettingsPage";
import { localize } from "client_legacy/ndaw/culture";
import {
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	SibiConnectionSaveModel,
	SibiConnectionViewModel,
	deleteConfiguration,
	getConfiguration,
	setConfiguration
} from "client/Api/Sibi/sibiConfiguration";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import {
	Checkbox,
	Form,
	FormItem,
	InputLike,
	InputRow,
	SecretValueInput,
	TextInput,
	ToolbarAction,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { SecretValue } from "client/Api/Apps/common";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const SibiConnectionPage = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [isEnabled, setIsEnabled] = useState<boolean | null>(null);

	const [lastSavedModel, setLastSavedModel] = useState<OptionalModel<SibiConnectionViewModel>>({ isSet: false });

	const [model, setModel] = useState<SibiConnectionSaveModel>(
		(): SibiConnectionSaveModel => ({
			subdomain: "",
			initializationVector: "",
			encryptionKey: {
				containsNewValue: true,
				value: ""
			}
		})
	);

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	useActiveSettingsMenuItem(SettingsPageKind.Sibi);

	const formHandler: IFormHandler<SibiConnectionSaveModel> = useFormHandler(
		model,
		setModel,
		validationConfiguration
	);

	const applyConfiguration = useCallback(
		(configuration: OptionalModel<SibiConnectionViewModel>): void => {
			if (configuration.isSet) {
				setIsEnabled(true);

				const { subdomain, initializationVector } = configuration.value;

				setModel({
					subdomain,
					initializationVector,
					encryptionKey: {
						containsNewValue: false,
						value: null
					}
				});
			} else {
				setIsEnabled(false);
			}
		},
		[]
	);

	useEffect(
		(): (() => void) => {
			const load = async (signal: AbortSignal): Promise<void> => {
				setIsBusy(true);

				try {
					const configuration: OptionalModel<SibiConnectionViewModel> = await getConfiguration(signal)
						.catch(handlePageDataLoadingError);

					setLastSavedModel(configuration);
					applyConfiguration(configuration);
					setIsBusy(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			};

			const cancellation = new AbortController();
			load(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager, applyConfiguration]
	);

	const cancelHandler = useCallback(
		(): void => {
			applyConfiguration(lastSavedModel);

			formHandler
				.validation.validate();
		},
		[
			applyConfiguration,
			formHandler,
			lastSavedModel
		]
	);

	const saveHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const { validation } = formHandler;
			try {
				if (isEnabled) {
					const validationResult: InputValidationResult = await validation.validate(globalCancellation);

					if (validationResult.kind !== InputValidationResultKind.Valid) {
						setIsBusy(false);
						return;
					}

					await setConfiguration(model, globalCancellation);

					setLastSavedModel({
						isSet: true,
						value: {
							subdomain: model.subdomain,
							initializationVector: model.initializationVector
						}
					});
				} else {
					await deleteConfiguration(globalCancellation);
					setLastSavedModel({ isSet: false });
					setModel({
						subdomain: "",
						initializationVector: "",
						encryptionKey: {
							containsNewValue: true,
							value: ""
						}
					});
				}

				alertManager.showSuccess(localize("_ChangesSaved"));
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!(isEnabled && validation.tryProcessServerError(e))) {
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			setIsBusy(false);
		},
		[
			alertManager,
			formHandler,
			globalCancellation,
			isBusy,
			isEnabled,
			model
		]
	);

	const defaultActions = useMemo(
		(): readonly SibiConnectionAction[] => listDefaultActions({
			cancel: cancelHandler,
			save: saveHandler
		}),
		[cancelHandler, saveHandler]
	);

	const topBarActions = useMemo(
		(): readonly SibiConnectionAction[] => {
			return buildActionsForTopBar<undefined, SibiConnectionAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	const enforceToSupplyEncryptionKey = useCallback(
		(): void => {
			const attachable: IValueInputAttachable<SecretValue> = formHandler.for("encryptionKey");

			const { containsNewValue } = attachable.value;
			if (containsNewValue) {
				return;
			}

			attachable.reducer
				.setValue({
					containsNewValue: true,
					value: ""
				});

			attachable.validation
				.triggerInputFinished();
		},
		[formHandler]
	);

	return (
		<SettingsPage
			hasBusyIndicator={isBusy}
			actions={topBarActions}
			actionContext={undefined}
			title={localize("_SibiConnection")}
		>
			{(isEnabled !== null) && (
				<SettingsPage.Section>
					<Form
						onSubmit={saveHandler}
						hasWidthConstrained
					>

						<FormItem>
							<Checkbox
								text={localize("_EnableSibiPop")}
								value={isEnabled}
								onChange={setIsEnabled}
							/>
						</FormItem>

						{isEnabled && (
							<>
								<FormItem label={localize("_DomainName")} isRequired>
									<InputRow>
										<InputLike value="https://" />
										<InputRow.Stretch>
											<TextInput
												{...formHandler.for("subdomain")
													.attachToTextInput()
													.withOnChange(enforceToSupplyEncryptionKey)
												}
											/>
										</InputRow.Stretch>
										<InputLike value=".sibi.nl" />
									</InputRow>

									<ValidationError {...formHandler.for("subdomain").attachToValidationError()} />
								</FormItem>

								<FormItem label={localize("_InitializationVector")} isRequired>
									<TextInput
										{...formHandler.for("initializationVector")
											.attachToTextInput()
											.withOnChange(enforceToSupplyEncryptionKey)
										}
									/>

									<ValidationError {...formHandler.for("initializationVector").attachToValidationError()} />
								</FormItem>

								<FormItem label={localize("_EncryptionKey")} isRequired>
									<SecretValueInput
										{...formHandler.for("encryptionKey").attachToSecretValueInput()}
										containsInitialValue={lastSavedModel.isSet}
									/>

									<ValidationError
										{...formHandler.for("encryptionKey")
											.for("value")
											.attachToValidationError()
										}
									/>
								</FormItem>
							</>
						)}
					</Form>
				</SettingsPage.Section>
			)}
			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
SibiConnectionPage.displayName = "SibiConnectionPage";

const validationConfiguration = createValidationConfiguration<SibiConnectionSaveModel>();

validationConfiguration.forScalar("subdomain")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

validationConfiguration.forScalar("initializationVector")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validationConfiguration.forObject("encryptionKey")
	.when((x) => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

type SibiConnectionActionContext = undefined;
type SibiConnectionAction = ToolbarAction<SibiConnectionActionContext>;

type DefaultActionsHandlers = {
	save: () => void;
	cancel: () => void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): readonly SibiConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save",
			title: localize("_Save"),
			icon: "floppy-disk",
			canExecute: () => true,
			execute: () => handlers.save(),
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel",
			title: localize("_Cancel"),
			icon: "xmark",
			canExecute: () => true,
			execute: () => handlers.cancel()
		}
	];
}

export const sibiConfigurationRouting = {
	page: "/SibiConfiguration"
};